import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ConfirmProvider from './components/UI/ConfirmProvider';
import IdleSessionGuard from './components/IdleSessionGuard';
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './routes/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import MentorRegistration from './pages/auth/MentorRegistration';
import ProfileSetup from './pages/user/ProfileSetup';
import VerifyEmail from './pages/user/VerifyEmail';
import Dashboard from './pages/user/Dashboard';
import ProfilePage from './pages/user/ProfilePage';
import Contact from './pages/Contact';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import AcceptLegalPage from './pages/legal/AcceptLegalPage';
import AdminRoute from './routes/AdminRoute';
import ContentAdminDashboard from './pages/admin/ContentAdminDashboard';
import ReviewQueuePage from './pages/admin/ReviewQueuePage';
import CategoriesPage from './pages/admin/CategoriesPage';
import LlmConfigPage from './pages/admin/LlmConfigPage';
import ContentGeneratorPage from './pages/admin/ContentGeneratorPage';
import AuthorsPage from './pages/admin/AuthorsPage';
import CoursesPage from './pages/content/CoursesPage';
import CourseLandingPage from './pages/content/CourseLandingPage';
import CoursePaymentPage from './pages/content/CoursePaymentPage';
import CourseTopicPage from './pages/content/CourseTopicPage';
import CourseAssessmentPage from './pages/content/CourseAssessmentPage';
import LabsPage from './pages/content/LabsPage';
import LabDetailPage from './pages/content/LabDetailPage';
import LabTopicPage from './pages/content/LabTopicPage';
import MyCoursesPage from './pages/content/MyCoursesPage';
import MyLearningPage from './pages/content/MyLearningPage';
import CourseEditorPage from './pages/content/CourseEditorPage';
import LabEditorPage from './pages/content/LabEditorPage';
import ProjectsPage from './pages/content/ProjectsPage';
import ProjectDetailPage from './pages/content/ProjectDetailPage';
import MyProjectsPage from './pages/content/MyProjectsPage';
import ProjectEditorPage from './pages/content/ProjectEditorPage';
import GigsPage from './pages/content/GigsPage';
import GigDetailPage from './pages/content/GigDetailPage';
import GigEditorPage from './pages/content/GigEditorPage';
import MyGigsPage from './pages/content/MyGigsPage';
import EventsPage from './pages/content/EventsPage';
import EventDetailPage from './pages/content/EventDetailPage';
import EventEditorPage from './pages/content/EventEditorPage';
import MyEventsPage from './pages/content/MyEventsPage';
import LinkedInCallback from './pages/auth/LinkedInCallback';
import GoogleCallback from './pages/auth/GoogleCallback';
import MentorVerifyEmail from './pages/auth/MentorVerifyEmail';
import InterviewerRegistration from './pages/auth/InterviewerRegistration';
import InterviewerVerifyEmail from './pages/auth/InterviewerVerifyEmail';
import InterviewsHome from './pages/interviews/InterviewsHome';
import InterviewDetailPage from './pages/interviews/InterviewDetailPage';
import AdminScheduleInterviewPage from './pages/admin/AdminScheduleInterviewPage';
import ProgramDetail from './pages/programs/ProgramDetail';
import ProgramsList from './pages/programs/ProgramsList';

function RedirectToCourseEdit() {
  const { id } = useParams();
  return <Navigate to={`/my-courses/${id}/edit`} replace />;
}

function RedirectAcademyProgram() {
  const { programId } = useParams();
  return <Navigate to={programId ? `/programs/${programId}` : '/programs'} replace />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ConfirmProvider>
        <IdleSessionGuard />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<MainLayout />}>
            <Route index element={<Home />} />
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="auth/linkedin/callback" element={<LinkedInCallback />} />
            <Route path="auth/google/callback" element={<GoogleCallback />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="mentor-verify-email" element={<MentorVerifyEmail />} />
            <Route path="interviewer-verify-email" element={<InterviewerVerifyEmail />} />
            <Route path="contact" element={<Contact />} />
            <Route path="terms" element={<TermsOfServicePage />} />
            <Route path="privacy" element={<PrivacyPolicyPage />} />
            <Route path="programs/ai-engineer" element={<ProgramDetail />} />
            <Route path="programs/:programId" element={<ProgramDetail />} />
            <Route path="programs" element={<ProgramsList />} />
            <Route path="interviews" element={<InterviewsHome />} />
            <Route path="interviews/request" element={<Navigate to="/interviews" replace />} />
            <Route path="ai-files" element={<Navigate to="/courses" replace />} />
            <Route path="ai-files/:slug" element={<Navigate to="/courses" replace />} />
            <Route path="courses/programs/:programId" element={<RedirectAcademyProgram />} />
            <Route path="courses/programs" element={<Navigate to="/programs" replace />} />
            <Route path="courses" element={<CoursesPage />} />
            <Route path="courses/:slug" element={<CourseLandingPage />} />
            <Route path="courses/:slug/topics/:topicSlug" element={<CourseTopicPage />} />
            <Route path="labs" element={<LabsPage />} />
            <Route path="labs/:slug" element={<LabDetailPage />} />
            <Route path="labs/:slug/topics/:topicSlug" element={<LabTopicPage />} />
            <Route path="training" element={<Navigate to="/programs" replace />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/:slug" element={<ProjectDetailPage />} />
            <Route path="gigs" element={<GigsPage />} />
            <Route path="gigs/:slug" element={<GigDetailPage />} />
            <Route path="events" element={<EventsPage />} />
            <Route path="events/:slug" element={<EventDetailPage />} />

            <Route element={<ProtectedRoute />}>
              <Route path="accept-legal" element={<AcceptLegalPage />} />
              <Route path="mentor-registration" element={<MentorRegistration />} />
              <Route path="interviewer-registration" element={<InterviewerRegistration />} />
              <Route path="profile-setup" element={<ProfileSetup />} />
              <Route path="verify-email" element={<VerifyEmail />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="interviews/:id" element={<InterviewDetailPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="profile/edit" element={<ProfilePage />} />
              <Route path="courses/:slug/payment" element={<CoursePaymentPage />} />
              <Route path="courses/:slug/assessments/:assessmentId" element={<CourseAssessmentPage />} />
              <Route path="my-courses" element={<MyCoursesPage />} />
              <Route path="my-courses/new" element={<CourseEditorPage />} />
              <Route path="my-courses/:id/edit" element={<CourseEditorPage />} />
              <Route path="my-articles" element={<Navigate to="/my-courses" replace />} />
              <Route path="my-articles/new" element={<Navigate to="/my-courses/new" replace />} />
              <Route path="my-articles/:id/edit" element={<RedirectToCourseEdit />} />
              <Route path="my-learning" element={<MyLearningPage />} />
              <Route path="my-projects" element={<MyProjectsPage />} />
              <Route path="my-gigs" element={<MyGigsPage />} />
              <Route path="my-events" element={<MyEventsPage />} />
            </Route>

            <Route element={<AdminRoute />}>
              <Route path="admin/dashboard" element={<ContentAdminDashboard />} />
              <Route path="admin/interviews/new" element={<AdminScheduleInterviewPage />} />
              <Route path="admin/review" element={<ReviewQueuePage />} />
              <Route path="admin/categories" element={<CategoriesPage />} />
              <Route path="admin/llm" element={<LlmConfigPage />} />
              <Route path="admin/generate" element={<ContentGeneratorPage />} />
              <Route path="admin/authors" element={<AuthorsPage />} />
              <Route path="admin/users" element={<AuthorsPage />} />
              <Route path="admin/labs/new" element={<LabEditorPage />} />
              <Route path="admin/labs/:id/edit" element={<LabEditorPage />} />
              <Route path="admin/projects/new" element={<ProjectEditorPage />} />
              <Route path="admin/projects/:id/edit" element={<ProjectEditorPage />} />
              <Route path="admin/gigs/new" element={<GigEditorPage />} />
              <Route path="admin/gigs/:id/edit" element={<GigEditorPage />} />
              <Route path="admin/events/new" element={<EventEditorPage />} />
              <Route path="admin/events/:id/edit" element={<EventEditorPage />} />
              <Route path="admin/school" element={<Navigate to="/admin/dashboard?tab=school-programs" replace />} />
              <Route path="admin/courses" element={<Navigate to="/admin/dashboard?tab=school-programs" replace />} />
              <Route path="owner/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        </ConfirmProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
